import { filter, map, merge, pipe, share, tap } from 'wonka';
import { Exchange, makeErrorResult } from 'urql';
import {
  startCurrentPrivilegedSession,
  getCurrentPrivilegedSession,
} from '../utils/privilege/privilegeStore';
import { getCurrentDevEnv } from '../utils/devEnv';

/** Default to 30 minutes */
const DEFAULT_SESSION_DURATION_MS = 30 * 60 * 1000;

const MAGIC_CATCHPHRASE = 'devproxy';

/**
 * Check whether we should prompt to start a privileged
 * access (writes enabled) session.
 *
 * For now, run only in devproxy - in the future, we may
 * want to extend (either here or similar) to production
 * as well to create more comprehensive access flows for
 * watershed internal users.
 *
 * https://www.notion.so/watershedclimate/rfc-devproxy-fast-not-furious-4596a003e2da4d1fa030dc002118ce1d?pvs=4
 *
 * @returns whether we should prompt for a privileged session
 */
const getShouldCheckPrivilegeSession = () => {
  return getCurrentDevEnv() === 'dev-proxy';
};

const privilegeExchange: () => Exchange = () => {
  let isAllowedToMutate = false;

  return ({ forward }) => {
    return (ops$) => {
      // Bail out - sanity check when in prod.
      if (!getShouldCheckPrivilegeSession()) {
        return forward(ops$);
      }

      const shared = pipe(ops$, share);

      const blockedMutations = pipe(
        shared,
        // If we're not allowed to mutate, then convert these operations to errors.
        filter((op) => op.kind === 'mutation' && !isAllowedToMutate),
        map((op) =>
          makeErrorResult(
            op,
            new Error('Mutations not allowed'),
            // hacky-ish?: We simulate a 403 error to prevent the retry exchange
            // from attempting to retry the mutation and triggering tons of prompts.
            new Response(undefined, { status: 403 })
          )
        )
      );

      const everythingElse = pipe(
        shared,
        tap((op) => {
          if (op.kind !== 'mutation') {
            return;
          }

          const session = getCurrentPrivilegedSession();

          // An active session means mutations can go through!
          if (session.getIsActive()) {
            isAllowedToMutate = true;
            return;
          }

          // Otherwise, let's prompt for affirmation.
          const affirmation = prompt(
            `🚨 You are about to mutate production data via devproxy! 🚨\nTo continue, write: "${MAGIC_CATCHPHRASE}"`
          );
          if (affirmation?.toLowerCase() !== MAGIC_CATCHPHRASE) {
            alert('Session not started, skipping mutations');
            isAllowedToMutate = false;
            return;
          }

          const currentSession = startCurrentPrivilegedSession(
            DEFAULT_SESSION_DURATION_MS
          );
          alert(
            `Session started - ending at ${new Date(
              currentSession?.endMs
            ).toLocaleTimeString()}`
          );
          isAllowedToMutate = true;
        }),
        filter((op) => (isAllowedToMutate ? true : op.kind !== 'mutation'))
      );
      return merge([forward(everythingElse), blockedMutations]);
    };
  };
};

export default privilegeExchange;
